import React from 'react';
import { Typography } from 'antd';
import DataCard from './page/DataCard';
import { RadialGauge, StackedBar, BarList, pctOf, isNum } from './charts';
import { formatDdMmYyyy } from '../utils/formatDdMmYyyy';
import { INFOGRAPHIC_SECTIONS, chartableTiles, tileNumber, latestComputedAt } from '../utils/dashboardInfographics';

const { Text } = Typography;

/**
 * One infographic card for the Control delivery / Evidence / Team sections. It charts only the tiles
 * the server says are available; the rest of the section keeps rendering as ordinary tiles (with their
 * own "not available" reason), so a missing metric is never drawn as a zero.
 */
function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} style={{ minWidth: 96 }}>
      <div style={{ fontSize: 24, fontWeight: 600, lineHeight: 1.2 }}>{value}</div>
      <Text type="secondary">{label}</Text>
    </div>
  );
}

function Delivery({ n }) {
  const inScope = n['gap.controls_in_scope'];
  const implemented = n['control.implemented_count'];
  const inReview = n['control.in_review_count'];
  const approved = n['control.approved_this_month'];
  const readiness = n['gap.readiness_percent'];

  let bar = null;
  const parts = [
    isNum(implemented) && { key: 'implemented', label: 'Implemented', value: implemented, color: 'var(--chart-2)' },
    isNum(inReview) && { key: 'review', label: 'In review', value: inReview, color: 'var(--chart-1)' },
  ].filter(Boolean);
  const used = parts.reduce((a, p) => a + p.value, 0);
  if (isNum(inScope) && inScope > 0 && parts.length && used <= inScope) {
    // Implemented and in-review are shown as slices of the in-scope total; the remainder is "Not yet in review or implemented".
    bar = <StackedBar label="Controls in scope" total={inScope} segments={[...parts, { key: 'rest', label: 'Other in-scope controls', value: inScope - used, color: 'var(--chart-3)' }]} testId="infographic-delivery-bar" />;
  } else if (parts.length || isNum(inScope)) {
    // Counts that cannot be proven to nest inside the in-scope total are drawn side by side, never stacked.
    const rows = [
      isNum(inScope) && { key: 'scope', label: 'In scope', value: inScope },
      ...parts.map((p) => ({ key: p.key, label: p.label, value: p.value })),
    ].filter(Boolean);
    bar = <BarList label="Control delivery" rows={rows} testId="infographic-delivery-bars" />;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
      {isNum(readiness) && <RadialGauge value={readiness} label="Gap readiness" caption="Gap readiness" testId="infographic-readiness" />}
      <div style={{ flex: '1 1 260px', minWidth: 0 }}>
        {bar}
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 16 }}>
          {isNum(inScope) && <Stat label="Controls in scope" value={inScope} testId="stat-in-scope" />}
          {isNum(approved) && <Stat label="Approved this month" value={approved} testId="stat-approved" />}
        </div>
      </div>
    </div>
  );
}

function Evidence({ n }) {
  const total = n['evidence.documents_count'];
  const scored = n['evidence.documents_scored'];
  const incidents = n['evidence.incident_count'];
  const rows = [
    isNum(total) && { key: 'docs', label: 'Evidence documents', value: total, max: total > 0 ? total : 1, valueText: String(total) },
    isNum(scored) && isNum(total) && { key: 'scored', label: 'Documents scored', value: scored, max: total > 0 ? total : 1, valueText: `${scored} of ${total}${pctOf(scored, total) != null ? ` (${pctOf(scored, total)}%)` : ''}` },
    isNum(scored) && !isNum(total) && { key: 'scored', label: 'Documents scored', value: scored, max: scored > 0 ? scored : 1, valueText: String(scored) },
  ].filter(Boolean);
  return (
    <div>
      {rows.length > 0 && <BarList label="Evidence documents and scoring" rows={rows} testId="infographic-evidence-bars" />}
      {isNum(incidents) && (
        <div style={{ marginTop: rows.length ? 16 : 0 }}>
          <Stat label="Incidents recorded (total)" value={incidents} testId="stat-incidents" />
        </div>
      )}
    </div>
  );
}

function Team({ n }) {
  const responsive = n['gap.delegates_responsive'];
  const advisors = n['gap.advisors_engaged'];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center' }}>
      {isNum(responsive) && <RadialGauge value={responsive} label="Delegates responsive" caption="Delegates responsive" testId="infographic-responsive" />}
      {isNum(advisors) && <Stat label="Advisors engaged" value={advisors} testId="stat-advisors" />}
    </div>
  );
}

const VIEWS = { delivery: Delivery, evidence: Evidence, team: Team };

const DashboardSectionInfographic = ({ sectionKey, tiles, branding }) => {
  const kind = INFOGRAPHIC_SECTIONS[sectionKey];
  const chartable = chartableTiles(sectionKey, tiles);
  if (!kind || chartable.size === 0) return null;
  const n = {};
  chartable.forEach((t, id) => { n[id] = tileNumber(t); });
  const View = VIEWS[kind];
  const asOf = latestComputedAt([...chartable.values()]);
  const style = {
    ...(branding?.primaryColor ? { borderTop: `3px solid ${branding.primaryColor}` } : {}),
    ...(branding?.fontFamily ? { fontFamily: branding.fontFamily } : {}),
  };
  return (
    <DataCard size="small" style={style} data-testid={`infographic-${sectionKey}`}>
      <View n={n} />
      {asOf && <Text type="secondary" data-testid="infographic-as-of" style={{ display: 'block', marginTop: 12 }}>As of {formatDdMmYyyy(asOf)}</Text>}
    </DataCard>
  );
};

export default DashboardSectionInfographic;
